import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { ActionStageEnum } from '../../models/enums/action-stage-enum';
import { TaskTypeEnum } from '../../models/enums/task-type-enum';
import { RaciService } from '../../services/raci.service';
import { EventBusService } from '../../../../services/event-bus.service';

/**
 * Handles raci steps pane component
 */
@Component({
  selector: 'raci-steps-pane',
  templateUrl: './raci-steps-pane.component.html',
  styleUrls: ['./raci-steps-pane.component.css'],
})

export class RaciStepsPaneComponent implements OnInit, OnDestroy {
  /**
 * Input property: steps
 * Description: The list of steps.
 */
  @Input() steps;

  /**
   * Input property: hasChildren
   * Description: Indicates if the steps have children.
   */
  @Input() hasChildren;

  /**
   * Input property: activeStep
   * Description: The active step.
   */
  @Input() activeStep;

  /**
   * Input property: activeTask
   * Description: The active task.
   */
  @Input() activeTask;

  /**
   * Input property: shouldShowConfirmDialog
   * Description: Indicates if a confirmation dialog should be shown.
   */
  @Input() shouldShowConfirmDialog;

  /**
   * Input property: activePortfolio
   * Description: The active portfolio.
   */
  @Input() activePortfolio;

  /**
   * Output property: onChangeActiveStep
   * Description: Event emitter for changing the active step.
   */
  @Output() onChangeActiveStep: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Output property: onChangeActiveTask
   * Description: Event emitter for changing the active task.
   */
  @Output() onChangeActiveTask: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Output property: onCompleteStep
   * Description: Event emitter for completing a step.
   */
  @Output() onCompleteStep: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Output property: onCompleteFunctionTask
   * Description: Event emitter for completing a function task.
   */
  @Output() onCompleteFunctionTask: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Property: isHistoryIconVisible
   * Description: Indicates if the history icon is visible.
   */
  isHistoryIconVisible = false;

  /**
   * Property: _subscriptions
   * Description: Array to store subscriptions.
   */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();

  /**
   * Property: isProjectClosed
   * Description: Indicates if the project is closed.
   */
  isProjectClosed = false;

  /**
   * Constructor
   * @param _ref ChangeDetectorRef
   * @param _raciService RaciService
   * @param _raciNavigationService RaciNavigationService
   */
  constructor(private eventBus: EventBusService,

    /**
    * Property: _ref
    */
    private _ref: ChangeDetectorRef,

    /**
     * Property: _raciService
     */
    private _raciService: RaciService,

    /**
     * Property: _raciNavigationService
     */
    private _raciNavigationService: RaciNavigationService) {
  }

  /**
   * OnInit
   */
  ngOnInit(): void {
    this._subscriptions.push(this._raciNavigationService._stepsResetShouldConfirmDialog.subscribe(resetValue => {
      this.shouldShowConfirmDialog = resetValue;
      this._ref.markForCheck();
    }))

    this._subscriptions.push(this._raciNavigationService._isProjectClosed.subscribe(value => {
      this.isProjectClosed = value;
      this._ref.markForCheck();
    }));
  }

  /**
   * OnDestroy
   * */
  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
  * Gets the Action Stage enum
  */
  public get actionStage(): typeof ActionStageEnum {
    return ActionStageEnum;
  }

  /**
  * Gets the Task Type enum
  */
  public get taskTypes(): typeof TaskTypeEnum {
    return TaskTypeEnum;
  }

  /**
  * Sets active step and shows step tasks if there is any
  * @param activeStep Active step
  */
  toggleStep(activeStep: any) {
    this._raciNavigationService.onDisplayApprovalHistory(false);
    this._raciNavigationService.onDisplayTaskApprovalHistoryDialog(false);
    if (!this.shouldShowConfirmDialog) {
      this.activeStep = activeStep;
      if (this.activeStep.tasks?.length > 0) {
        this.setActiveTask(this.activeStep.tasks[0]);
      }
      else {
        this.setActiveTask(null);
      }

      this.onChangeActiveStep.emit(this.activeStep);
      this.onChangeActiveTask.emit(this.activeTask);
      this._raciNavigationService.onSelectRow(false);
      this._raciNavigationService.onShowApproveIcon(false);

    }
    else {
      this.displayConfirmDialog();
    }
  }

  /**
  * Sets active task
  * @param task Active task
  */
  setActiveTask(task: any) {
    this._raciNavigationService.onDisplayApprovalHistory(false);
    this._raciNavigationService.onDisplayTaskApprovalHistoryDialog(false);
    if (!this.shouldShowConfirmDialog) {
      this.activeTask = task;
      this.onChangeActiveTask.emit(this.activeTask);
    }
    else if (this.activeTask?.projectTaskId != task.projectTaskId) {
      this.displayConfirmDialog();
    }
    this._raciNavigationService.onSelectRow(false);
    this._raciNavigationService.onShowApproveIcon(false);
    this.eventBus.broadcastEvent("isTaskInProgress", task?.taskActionStage === ActionStageEnum.Waiting);
    this._raciNavigationService.isGeneralTaskActive(false);
  }

  /**
   * Mark step as completed
   * @param selectedStep Step info
   */
  completeStep(selectedStep: any) {
    if (this.checkCompletingStep(selectedStep)) {
      this.openDialogForCompleatingStep(selectedStep);
    }
  }

  /**
  * Mark step as completed
  * @param selectedTask Step info
  */
  completeTask(selectedTask: any) {
    if (this.checkCompletingTask(selectedTask)) {
      this.openDialogForCompletingFunctionTask(selectedTask);
    }
  }

  /**
   * Check if step can be completed
   * @param selectedStep Selected step
   * */
  checkCompletingStep(selectedStep: any) {
    return (!selectedStep?.isCompleted && !selectedStep?.tasks?.find(task => !task.isCompleted)) || (selectedStep?.isCompleted && !selectedStep?.relatedSteps?.find(step => !step.isCompleted) && !selectedStep?.tasks?.find(task => !task.isCompleted));
  }

  /**
   * Check if step can be completed
   * @param selectedTask Selected step
   * */
  checkCompletingTask(selectedTask: any) {
    return (!selectedTask?.isCompleted && !selectedTask?.tasks?.find(task => !task.isCompleted)) || (selectedTask?.isCompleted && !selectedTask?.relatedTasks?.find(task => !selectedTask.isCompleted) && !selectedTask?.tasks?.find(task => !task.isCompleted));
  }

  /**
   * Open confirm dialog for completing step
   * @param step Current step
   * */
  openDialogForCompleatingStep(step: any) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        this.onCompleteStep.emit(step);
      },
      cancel: () => {
        //Nothing
      },
    }, 'completeRaciStep');
  }

  /**
 * Open confirm dialog for completing task
 * @param task Current step
 * */
  openDialogForCompletingFunctionTask(task: any) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        this.onCompleteFunctionTask.emit(task);
      },
      cancel: () => {
        //Nothing
      },
    }, 'completeRaciTask');
  }

  /**
   * Update step action stage
   * @param selectedStep Step info
   */
  updateStepActionStage(selectedStep: any) {
    if (this.isProjectClosed || this.activePortfolio.isFinalized) return;

    if (selectedStep?.actionStageId == ActionStageEnum.Waiting && !selectedStep?.relatedSteps?.find(step => !step.isCompleted)) {
      this.completeStep(selectedStep);
    }
    else if (selectedStep?.actionStageId == ActionStageEnum.Open || selectedStep?.actionStageId == ActionStageEnum.Undefined) {
      this._raciService.updateStepActionStage(selectedStep?.projectStepId).subscribe(data => {
        if (data) {
          selectedStep.actionStageId = ActionStageEnum.Waiting;
        }
      });
    }
  }

  /**
   * Updates task action stage
   * @param selectedTask Task info
   * */
  updateTaskActionStage(selectedTask: any) {
    this.setActiveTask(this.activeTask);
    this.activeTask = selectedTask;
    if (this.isProjectClosed) return;
    if (this.activePortfolio.isFinalized && selectedTask.taskActionStage == ActionStageEnum.Undefined) return;

    if (selectedTask.approvalMatrix.length < 1 && this.isApprovalTask(selectedTask)) return;

    if (selectedTask.taskActionStage == ActionStageEnum.Undefined) {
      this.subscribeToUpdateTaskActionStage(selectedTask);
    }
    else if (selectedTask.taskActionStage == ActionStageEnum.Waiting && !this.isApprovalTask(selectedTask) && !selectedTask?.relatedTasks?.find(task => !task.isCompleted)) {
      this.completeTask(selectedTask);
    }


    this.onChangeActiveTask.emit(this.activeTask);
  }

  /**
   * Subscribes to update task action stage event
   * @param selectedTask Task that will be updated
   */
  private subscribeToUpdateTaskActionStage(selectedTask: any) {
    this._subscriptions.push(this._raciService.updateTaskActionStage(selectedTask?.projectTaskId, this.activeStep?.projectStepId).subscribe(data => {
      if (data) {
        selectedTask.taskActionStage = ActionStageEnum.Waiting;
        this.eventBus.broadcastEvent("isTaskInProgress", true);
        this._raciNavigationService.showSetupValuationEffectiveDatesSaveIcon(selectedTask?.taskTypeId === TaskTypeEnum.ValuationEffectiveDatesSetup);
        this._raciNavigationService.showSetupValuationEventSaveIcon(selectedTask?.taskTypeId === TaskTypeEnum.ValuationEventFunction);

        if (this.activeStep.actionStageId == ActionStageEnum.Open || this.activeStep.actionStageId == ActionStageEnum.Undefined) {
          this.updateStepActionStage(this.activeStep);
        }
      }
    }));
  }

  /**
 * Expands specified step
 * @param event Step click event
 * @param selectedStep Specified step that needs to be collapsed/expanded
 */
  expandStep(event: any, selectedStep: any) {
    if (!event.currentTarget.classList.contains("icon-toogle-rotate")) {
      this.displayExpandedIcon(event)
      this.activeStep = selectedStep;
    } else {
      this.displayCollapsedIcon(event);
      this.activeStep = null;
    }
    this.onChangeActiveStep.emit(this.activeStep);
  }

  /**
   * Displays confirmation dialog
   * */
  private displayConfirmDialog() {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        //Nothing
      }
    }, 'saveApproval');
  }

  /**
   * Sets the expansion icon as collapsed
   * @param event Step click event
   */
  private displayCollapsedIcon(event: any) {
    event.currentTarget.classList.remove("icon-toogle-rotate")
    event.currentTarget.classList.add("icon-toogle");
  }

  /**
  * Sets the expansion icon as expanded
  * @param event Step click event
  */
  private displayExpandedIcon(event: any) {
    event.currentTarget.classList.remove("icon-toogle")
    event.currentTarget.classList.add("icon-toogle-rotate");
  }

  /**
  * Checks if step is completed
  * @param step Step that will be checked
  */
  checkIfStepIsCompleted(step: any) {
    return step.isCompleted && !step.relatedSteps?.find(step => !step.isCompleted);
  }

  /**
   * Checks if task is completed
   * @param selectedTask Task that will be checked
   */
  checkIfTaskIsCompleted(selectedTask: any) {
    return selectedTask.isCompleted && !selectedTask.relatedTasks?.find(task => !selectedTask.isCompleted);
  }

  /**
   * Check if task is approval task or function task
   * @param task Selected task
   */
  isApprovalTask(task: any): boolean {
    return task?.taskTypeId != this.taskTypes.ValoremFunction && task?.taskTypeId != this.taskTypes.ArgusFunction && task.taskTypeId != this.taskTypes.ArgusSyncFunction && task.taskTypeId != this.taskTypes.ValuationEventFunction && task.taskTypeId != this.taskTypes.ValuationEffectiveDatesSetup;
  }
}
